import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../../components/Input/Input";
import Notification from "../../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./AddNotificationTemplate.module.scss";

/* =========================
   HELPERS
========================= */
// Build a code suggestion from a name:
// lowercase, spaces -> "-", strip special chars, collapse dashes.
const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const emptyTemplate = () => ({
  type: "Email",
  name: "",
  code: "",
  subject: "",
  message: "",
  // Tracks whether the user manually edited the code so we stop auto-filling it.
  codeEdited: false,
});

const AddNotificationTemplate = () => {
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [types, setTypes] = useState([]);
  const [activityLogCategories, setActivityLogCategories] = useState([]);

  const [templates, setTemplates] = useState([emptyTemplate()]);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  /* =========================
     HELPERS
  ========================= */
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     FETCH META
  ========================= */
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const response = await apiGet(
          "notification",
          "/template-masters/meta",
        );

        const data = response?.data;
        if (data) {
          if (Array.isArray(data.types)) {
            setTypes(data.types);
            // Default the first template to the first available type.
            setTemplates((prev) =>
              prev.map((tpl, i) =>
                i === 0 ? { ...tpl, type: data.types[0] || tpl.type } : tpl,
              ),
            );
          }
          if (Array.isArray(data.activityLogCategories)) {
            // De-duplicate categories (the API may return case variants).
            const unique = [...new Set(data.activityLogCategories)];
            setActivityLogCategories(unique);
          }
        }
      } catch (error) {
        console.error("Failed to fetch template meta", error);
        showNotification("Failed to load template options", "error");
      }
    };

    fetchMeta();
  }, []);

  /* =========================
     HANDLERS
  ========================= */
  const updateTemplate = (index, changes) => {
    setTemplates((prev) =>
      prev.map((tpl, i) => (i === index ? { ...tpl, ...changes } : tpl)),
    );
    // Clear errors for the field(s) being edited.
    setErrors((prev) =>
      prev.map((err, i) => {
        if (i !== index) return err;
        const next = { ...err };
        Object.keys(changes).forEach((key) => delete next[key]);
        return next;
      }),
    );
  };

  const handleNameChange = (index, value) => {
    setTemplates((prev) =>
      prev.map((tpl, i) => {
        if (i !== index) return tpl;
        return {
          ...tpl,
          name: value,
          // Keep code in sync with the name until the user edits it manually.
          code: tpl.codeEdited ? tpl.code : slugify(value),
        };
      }),
    );
    setErrors((prev) =>
      prev.map((err, i) => {
        if (i !== index) return err;
        const next = { ...err };
        delete next.name;
        if (!templates[index]?.codeEdited) delete next.code;
        return next;
      }),
    );
  };

  const handleTypeChange = (index, value) => {
    setTemplates((prev) =>
      prev.map((tpl, i) => {
        if (i !== index) return tpl;
        // Reset subject when toggling to/from ActivityLog since the field
        // changes between a dropdown and a free-text input.
        const wasActivityLog = tpl.type === "ActivityLog";
        const isActivityLog = value === "ActivityLog";
        return {
          ...tpl,
          type: value,
          subject: wasActivityLog !== isActivityLog ? "" : tpl.subject,
        };
      }),
    );
  };

  const addTemplate = () => {
    setTemplates((prev) => [
      ...prev,
      { ...emptyTemplate(), type: types[0] || "Email" },
    ]);
    setErrors((prev) => [...prev, {}]);
  };

  const removeTemplate = (index) => {
    setTemplates((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const allErrors = templates.map((tpl) => {
      const errs = {};
      if (!tpl.type) errs.type = "Type is required";
      if (!tpl.name.trim()) errs.name = "Name is required";
      if (!tpl.code.trim()) errs.code = "Code is required";
      if (!tpl.subject.trim()) errs.subject = "Subject is required";
      if (!tpl.message.trim()) errs.message = "Message is required";
      return errs;
    });

    setErrors(allErrors);
    return allErrors.every((errs) => Object.keys(errs).length === 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);

    try {
      const payload = {
        templates: templates.map((tpl) => ({
          type: tpl.type,
          code: tpl.code.trim(),
          name: tpl.name.trim(),
          subject: tpl.subject.trim(),
          message: tpl.message,
        })),
      };

      const res = await apiPost("notification", "/template-masters", payload);

      if (res?.status === "success") {
        showNotification(res?.message || "Templates processed", "success");
        setTimeout(() => navigate("/admin/notification-template"), 1500);
      } else {
        showNotification(res?.message || "Failed to create templates", "error");
      }
    } catch (err) {
      console.error("Template creation error:", err);
      showNotification(
        "Something went wrong while creating the templates",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["add-noti-temp__page"]}>
      {notification && (
        <div className={styles["add-noti-temp__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["add-noti-temp__header"]}>
        <button
          className={styles["add-noti-temp__back-btn"]}
          onClick={() => navigate(-1)}
          type="button"
          aria-label="Go Back"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>

        <div className={styles["add-noti-temp__title-group"]}>
          <h2 className={styles["add-noti-temp__title"]}>Add Template</h2>
          <p className={styles["add-noti-temp__subtitle"]}>
            Create one or more notification templates at once.
          </p>
        </div>
      </div>

      {/* FORM */}
      <form onSubmit={handleSubmit} noValidate>
        {templates.map((tpl, index) => {
          const isActivityLog = tpl.type === "ActivityLog";
          const err = errors[index] || {};

          return (
            <div className={styles["add-noti-temp__card"]} key={index}>
              <div className={styles["add-noti-temp__card-head"]}>
                <span className={styles["add-noti-temp__card-title"]}>
                  Template {index + 1}
                </span>
                {templates.length > 1 && (
                  <button
                    type="button"
                    className={styles["add-noti-temp__remove-btn"]}
                    onClick={() => removeTemplate(index)}
                    aria-label="Remove template"
                  >
                    <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                )}
              </div>

              <div className={styles["add-noti-temp__grid"]}>
                {/* Type */}
                <Input
                  type="select"
                  label="Type"
                  placeholder="Select a type"
                  value={tpl.type}
                  onChange={(e) => handleTypeChange(index, e.target.value)}
                  options={types.map((t) => ({ label: t, value: t }))}
                  error={err.type}
                  required
                />

                {/* Name */}
                <Input
                  type="text"
                  label="Name"
                  placeholder="e.g. Gift Credits Added"
                  value={tpl.name}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  error={err.name}
                  required
                />

                {/* Code (auto-filled from name, editable) */}
                <Input
                  type="text"
                  label="Code"
                  placeholder="e.g. gift-credits-added"
                  value={tpl.code}
                  onChange={(e) =>
                    updateTemplate(index, {
                      code: e.target.value,
                      codeEdited: true,
                    })
                  }
                  error={err.code}
                  required
                />

                {/* Subject — dropdown for ActivityLog, text otherwise */}
                {isActivityLog ? (
                  <Input
                    type="select"
                    label="Subject"
                    placeholder="Select a category"
                    value={tpl.subject}
                    onChange={(e) =>
                      updateTemplate(index, { subject: e.target.value })
                    }
                    options={activityLogCategories.map((c) => ({
                      label: c,
                      value: c,
                    }))}
                    error={err.subject}
                    required
                  />
                ) : (
                  <Input
                    type="text"
                    label="Subject"
                    placeholder="e.g. Welcome to our platform"
                    value={tpl.subject}
                    onChange={(e) =>
                      updateTemplate(index, { subject: e.target.value })
                    }
                    error={err.subject}
                    required
                  />
                )}

                {/* Message */}
                <div className={styles["add-noti-temp__full-width"]}>
                  <Input
                    type="textarea"
                    label="Message"
                    style={{ height: "140px" }}
                    placeholder="Use {{variables}} for dynamic content..."
                    value={tpl.message}
                    onChange={(e) =>
                      updateTemplate(index, { message: e.target.value })
                    }
                    error={err.message}
                    required
                  />
                </div>
              </div>
            </div>
          );
        })}

        {/* ADD ANOTHER TEMPLATE */}
        <button
          type="button"
          className={styles["add-noti-temp__add-btn"]}
          onClick={addTemplate}
        >
          <Plus size={18} strokeWidth={2.5} />
          Add Another Template
        </button>

        {/* ACTIONS */}
        <div className={styles["add-noti-temp__actions"]}>
          <button
            type="button"
            className={`${styles["add-noti-temp__btn"]} ${styles["add-noti-temp__btn--secondary"]}`}
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            <X size={16} strokeWidth={2.5} />
            Cancel
          </button>

          <button
            type="submit"
            className={`${styles["add-noti-temp__btn"]} ${styles["add-noti-temp__btn--primary"]}`}
            disabled={submitting}
          >
            <Save size={16} strokeWidth={2.5} />
            {submitting
              ? "Saving..."
              : `Save ${templates.length > 1 ? "Templates" : "Template"}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddNotificationTemplate;
